import type { ListBuilderConfiguration } from '../types';

/**
 * Pending Matches list (PD-13891) — the consolidated match-review surface that
 * replaces BOTH the old License Matching workspace (matches against an existing
 * client_license) and the Suggested list (orphan suggestions for clients with
 * no license yet). One row per pending `client_license_match`.
 *
 * Backed by GET /api/licenses/matches/list, which is rooted entirely on the
 * tenant `client_license_match` table and reads the registrant name from the
 * denormalized `registry_business_name`/`registry_dba_name` columns — so the
 * list filters/sorts/searches locally with NO cross-DB registry join.
 *
 * Field names below map to the flat row shape returned by that endpoint.
 */
export const LICENSE_MATCH_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-match-all',
			cacheTTL: 120000,
			name: 'All Pending',
			type: 'default',
			active: true,
			sortOrder: 0,
			filterRules: {},
			columns: [
				{
					id: 'match-client',
					fields: [
						{
							id: 'client-name-field',
							link: {
								type: 'custom',
								enabled: true,
								urlTemplate: '/clients/{clientId}',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientName',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
								// PD-14887: long client names rendered nowrap with NO
								// ellipsis, widening this column until the Conf. column
								// and the row action fell off-screen at 1440px with the
								// filter panel open. Single-line ellipsis bounds it.
								textExpansion: { enabled: true, maxLines: 1 },
							},
							displayStyle: 'text',
						},
						{
							id: 'client-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						// Full address, mirroring the client list's name column
						// (street line, then "city, state zip") so the two
						// surfaces present a client identically. The endpoint
						// already returns the flat clientAddress1/clientCity/
						// clientState/clientZip columns from the client's
						// shipping address.
						{
							id: 'client-address1-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientAddress1',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'client-lb-2',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'client-city-state-zip',
							dataConfig: {
								type: 'template',
								template: '{clientCity}, {clientState} {clientZip}',
								templateFields: [
									{ fieldName: 'clientCity' },
									{ fieldName: 'clientState' },
									{ fieldName: 'clientZip' },
								],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
							conditionalVisibility: {
								field: 'clientCity',
								condition: 'is_not_null',
							},
						},
					],
					label: 'Client',
					headers: [{ id: 'h-client', text: 'Client' }],
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'clientName',
							objectTable: 'client_license_match',
						},
					],
					minWidth: 220,
				},
				{
					// C#/L# identity badge — lifted out of the Client column into its
					// own column (PD-13980 refinement) so it sits immediately to the
					// right of the client name/address. Shows the client's EXTERNAL id
					// (`clientExternalId2` → `clientExternalId`), falling back to the
					// internal `clientId` only for leads with no external id — matching
					// the client list's `{externalId2|externalId|id}` tail.
					id: 'match-client-id',
					fields: [
						{
							id: 'lead-customer-badge',
							dataConfig: {
								type: 'template',
								template:
									'{clientLeadOrCustomer:Customer=C,Lead=L}#{clientExternalId2|clientExternalId|clientId}',
								templateFields: [
									{ fieldName: 'clientLeadOrCustomer' },
									{ fieldName: 'clientExternalId2' },
									{ fieldName: 'clientExternalId' },
									{ fieldName: 'clientId' },
								],
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'conditional',
									rules: [
										{
											field: { fieldName: 'clientLeadOrCustomer' },
											operator: 'equals',
											value: 'Customer',
											ignoreCase: true,
											color: { type: 'info' },
										},
										{
											field: { fieldName: 'clientLeadOrCustomer' },
											operator: 'equals',
											value: 'Lead',
											ignoreCase: true,
											color: { type: 'warning' },
										},
									],
								},
								variant: 'solid',
							},
							displayStyle: 'badge',
						},
					],
					label: 'C/L #',
					headers: [{ id: 'h-client-id', text: 'C/L #' }],
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					sortable: false,
					minWidth: 100,
				},
				{
					id: 'match-suggestion',
					fields: [
						{
							// PD-14997: `registryDisplayName` is a server projection of
							// the name the matchmaker ACTUALLY compared — the DBA when
							// the row earned its score on the DBA, otherwise the legal
							// name. Binding straight to `registryBusinessName` showed a
							// correct match as two unrelated names ("Fan's Family
							// Pharmacy" against "CENTRAL RX, LLC", whose DBA is "Fan's
							// Family Pharmacy"). Projected server-side for the same
							// reason as `registryLicenseTypeLabel` below: config columns
							// render field values verbatim and cannot call a resolver.
							id: 'registry-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'registryDisplayName',
									objectTable: 'client_license_match',
								},
								defaultValue: '(no name on record)',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
								// PD-14887: registrant names run as long as client names —
								// same single-line ellipsis bound (see client-name-field).
								textExpansion: { enabled: true, maxLines: 1 },
							},
							displayStyle: 'text',
						},
						{
							id: 'reg-dba-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							// "DBA of <legal name>", so the reviewer still sees who holds
							// the licence. Server-projected as an EMPTY string when the
							// match was not scored on a DBA, so this line collapses for
							// every other row (same pattern as `candidateCountLabel`).
							id: 'registry-dba-note-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'registryDbaNote',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								textExpansion: { enabled: true, maxLines: 1 },
							},
							displayStyle: 'text',
						},
						{
							id: 'reg-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							// `registryLicenseTypeLabel` is a server-projected display
							// label for the raw `registry_license_type` (free-text
							// varchar with ~582 distinct values, incl. NPI/NUCC taxonomy
							// codes) — projected by LicenseMatchReviewService
							// .listPendingMatches via humanizeLicenseType so the user
							// never sees the raw token. Config columns render field
							// values verbatim, so the humanizing happens server-side.
							id: 'registry-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'registryLicenseTypeLabel',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'registry-sep-1',
							dataConfig: { type: 'custom_text', customText: ' · ' },
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'registry-state-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'registryStateCode',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'registry-sep-2',
							dataConfig: { type: 'custom_text', customText: ' · #' },
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'registry-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'registryLicenseNumber',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'candidate-count-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							// PD-14515: the queue shows ONE row per work item — the
							// highest-confidence candidate. When alternatives exist,
							// this server-projected label ("+2 more candidates — best
							// shown") tells the rep the wizard will present the rest,
							// ranked. Empty string (the common case) renders nothing.
							id: 'candidate-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'candidateCountLabel',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Suggested Match',
					headers: [{ id: 'h-suggestion', text: 'Suggested Match' }],
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					sortable: false,
					minWidth: 240,
				},
				{
					id: 'match-kind',
					fields: [
						{
							id: 'new-license-badge',
							dataConfig: { type: 'custom_text', customText: 'New License' },
							displayStyle: 'badge',
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'warning' },
								},
							},
							conditionalVisibility: {
								field: 'isNewLead',
								condition: 'equals',
								value: true,
							},
						},
						{
							id: 'existing-license-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'existingLicenseNumber',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
							conditionalVisibility: {
								field: 'isNewLead',
								condition: 'equals',
								value: false,
							},
						},
					],
					label: 'Links To',
					headers: [{ id: 'h-kind', text: 'Links To' }],
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					sortable: false,
					width: 120,
				},
				{
					id: 'match-confidence',
					fields: [
						{
							id: 'confidence-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'confidenceScore',
									objectTable: 'client_license_match',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'confidence-suffix',
							dataConfig: { type: 'custom_text', customText: '%' },
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Conf.',
					headers: [{ id: 'h-confidence', text: 'Conf.' }],
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'confidenceScore',
							objectTable: 'client_license_match',
						},
					],
					width: 88,
				},
				{
					id: 'match-actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'review-match-action',
									// "Confirm" — matches the "Pending Confirmation"
									// tab/state this list now lives under (PD-13998):
									// the action opens the per-client wizard where the
									// rep confirms (or rejects) the suggestion.
									label: 'Confirm',
									iconName: 'clipboardCheck',
									callbackName: 'reviewMatch',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'licenses:update',
								},
							],
						},
					],
					label: '',
					headers: [{ id: 'h-actions', text: '' }],
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					sortable: false,
					width: 96,
				},
			],
			defaultSortId: 'column-match-confidence-desc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		// PD-14449: matchLeadOrCustomer / matchConfidence / matchNewLeadsOnly were
		// already wired in the backend (listPendingMatches) — exposing them here.
		activeFilters: [
			'matchKeywords',
			'matchState',
			'matchSource',
			'matchLeadOrCustomer',
			'matchConfidence',
			'matchNewLeadsOnly',
		],
	},
};
